import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles, Users, BookOpen, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data.slice(0, 3)); // Show top 3 courses
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div className="absolute inset-0 bg-[#f5f3f8] opacity-100" />
        <div className="container mx-auto px-6 py-24 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8" data-testid="hero-content">
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-gray-800">
                Nurturing Young Minds,
                <span className="block bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500 bg-clip-text text-transparent mt-2">Empowering Educators</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Nurturing emotionally resilient, self-aware GenZ learners who thrive in relationships and life. Providing teachers and parents with expert guidance, holistic strategies, and ready-made curriculum resource.
              </p>
              <div className="flex gap-4">
                <Button 
                  data-testid="explore-courses-btn"
                  onClick={() => navigate('/courses')}
                  className="bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full shadow-lg"
                >
                  Explore Courses <ArrowRight className="ml-2" />
                </Button>
                <Button 
                  data-testid="learn-more-btn"
                  onClick={() => navigate('/founder')}
                  variant="outline"
                  className="border-2 border-teal-600 text-teal-700 hover:bg-teal-50 px-8 py-6 text-lg rounded-full"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative" data-testid="hero-image">
              <img 
                src="https://images.unsplash.com/photo-1525829528215-ffae12a76ac8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxlYXJseSUyMGNoaWxkaG9vZCUyMGVkdWNhdGlvbiUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzYxMzk1OTA3fDA&ixlib=rb-4.1.0&q=85"
                alt="Early childhood education"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-4xl font-bold bg-gradient-to-r from-teal-500 to-blue-500 bg-clip-text text-transparent">5000+</div>
              <p className="text-gray-600">Parents & Educators</p>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">100+</div>
              <p className="text-gray-600">Expert-Led Lessons</p>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">10+</div>
              <p className="text-gray-600">Years Experience</p>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Global</div>
              <p className="text-gray-600">Reach & Impact</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-[#f5f3f8]" data-testid="mission-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800">Our Mission</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Tinkred is driven by passion, authenticity, creativity, and intelligence. We bridge the gap for women seeking to reach their full potential in early childhood education careers. Whether you want to open a small center or scale to a larger operation, we empower you with confidence and independence.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <Users className="w-12 h-12 text-[#a855f7] mb-4 mx-auto" />
                <h3 className="text-xl font-bold mb-3">Empower Educators</h3>
                <p className="text-gray-600">Equip teachers with research-backed strategies and practical tools</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <BookOpen className="w-12 h-12 text-[#14b8a6] mb-4 mx-auto" />
                <h3 className="text-xl font-bold mb-3">Holistic Development</h3>
                <p className="text-gray-600">Foster social, emotional, cognitive, and creative growth</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <Award className="w-12 h-12 text-[#ff9500] mb-4 mx-auto" />
                <h3 className="text-xl font-bold mb-3">Global Impact</h3>
                <p className="text-gray-600">Empowering global communities through education and innovation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Focus Areas */}
      <section className="py-20 bg-[#f5f3f8]" data-testid="core-focus-areas">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Our Core Focus Areas</h2>
            <p className="text-lg text-gray-600">Comprehensive programs designed to nurture growth at every stage</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Mental Health */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <linearGradient id="mentalHealthGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#14b8a6', stopOpacity: 1}} />
                      <stop offset="50%" style={{stopColor: '#3b82f6', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#a855f7', stopOpacity: 1}} />
                    </linearGradient>
                  </defs>
                  <path d="M50 75 L50 40 M35 50 Q35 35 50 35 Q65 35 65 50 L50 65 Z" fill="url(#mentalHealthGradient)" stroke="url(#mentalHealthGradient)" strokeWidth="3"/>
                  <circle cx="50" cy="25" r="12" fill="none" stroke="url(#mentalHealthGradient)" strokeWidth="3"/>
                  <path d="M45 22 Q50 27 55 22" fill="none" stroke="url(#mentalHealthGradient)" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Mental Health & Stress Management</h3>
              <p className="text-gray-600" style={{fontSize: '15px'}}>Comprehensive support for Gen Z tackling stress, anxiety, and emotional challenges</p>
            </div>

            {/* Emotional Intelligence */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <linearGradient id="emotionalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#14b8a6', stopOpacity: 1}} />
                      <stop offset="50%" style={{stopColor: '#3b82f6', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#a855f7', stopOpacity: 1}} />
                    </linearGradient>
                  </defs>
                  <circle cx="35" cy="35" r="15" fill="none" stroke="url(#emotionalGradient)" strokeWidth="3"/>
                  <circle cx="65" cy="35" r="15" fill="none" stroke="url(#emotionalGradient)" strokeWidth="3"/>
                  <circle cx="50" cy="65" r="15" fill="none" stroke="url(#emotionalGradient)" strokeWidth="3"/>
                  <line x1="45" y1="40" x2="55" y2="60" stroke="url(#emotionalGradient)" strokeWidth="3"/>
                  <line x1="40" y1="45" x2="35" y2="55" stroke="url(#emotionalGradient)" strokeWidth="3"/>
                  <line x1="60" y1="45" x2="65" y2="55" stroke="url(#emotionalGradient)" strokeWidth="3"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Emotional Intelligence & Social Skills</h3>
              <p className="text-gray-600" style={{fontSize: '15px'}}>Building self-awareness, empathy, and strong interpersonal connections</p>
            </div>

            {/* Early Childhood Education */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <linearGradient id="educationGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#14b8a6', stopOpacity: 1}} />
                      <stop offset="50%" style={{stopColor: '#3b82f6', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#a855f7', stopOpacity: 1}} />
                    </linearGradient>
                  </defs>
                  <rect x="25" y="30" width="50" height="45" rx="3" fill="none" stroke="url(#educationGradient)" strokeWidth="3"/>
                  <line x1="50" y1="30" x2="50" y2="75" stroke="url(#educationGradient)" strokeWidth="2"/>
                  <circle cx="65" cy="50" r="10" fill="url(#educationGradient)"/>
                  <path d="M60 47 L63 50 L68 45" fill="none" stroke="white" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Early Childhood Education</h3>
              <p className="text-gray-600" style={{fontSize: '15px'}}>Expert training for teachers and parents in holistic child development</p>
            </div>

            {/* Curriculum Frameworks */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <linearGradient id="curriculumGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor: '#14b8a6', stopOpacity: 1}} />
                      <stop offset="50%" style={{stopColor: '#3b82f6', stopOpacity: 1}} />
                      <stop offset="100%" style={{stopColor: '#a855f7', stopOpacity: 1}} />
                    </linearGradient>
                  </defs>
                  <rect x="25" y="20" width="50" height="60" rx="3" fill="none" stroke="url(#curriculumGradient)" strokeWidth="3"/>
                  <line x1="35" y1="35" x2="45" y2="35" stroke="url(#curriculumGradient)" strokeWidth="2"/>
                  <circle cx="52" cy="35" r="2" fill="url(#curriculumGradient)"/>
                  <line x1="35" y1="45" x2="45" y2="45" stroke="url(#curriculumGradient)" strokeWidth="2"/>
                  <circle cx="52" cy="45" r="2" fill="url(#curriculumGradient)"/>
                  <line x1="35" y1="55" x2="45" y2="55" stroke="url(#curriculumGradient)" strokeWidth="2"/>
                  <circle cx="52" cy="55" r="2" fill="url(#curriculumGradient)"/>
                  <line x1="35" y1="65" x2="45" y2="65" stroke="url(#curriculumGradient)" strokeWidth="2"/>
                  <circle cx="52" cy="65" r="2" fill="url(#curriculumGradient)"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Curriculum Frameworks</h3>
              <p className="text-gray-600" style={{fontSize: '15px'}}>Ready-to-use lesson plans and comprehensive educational frameworks</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Tinkred */}
      <section className="py-20 bg-[#f5f3f8]" data-testid="why-tinkred-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Why Choose Tinkred</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#a855f7] rounded-full flex items-center justify-center mx-auto">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">Expert-Led Content</h3>
              <p className="text-gray-600">Developed by experienced early childhood educators and psychologists</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#14b8a6] rounded-full flex items-center justify-center mx-auto">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">Flexible Learning</h3>
              <p className="text-gray-600">Self-paced modules accessible anytime, anywhere</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#ff9500] rounded-full flex items-center justify-center mx-auto">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">Comprehensive Resources</h3>
              <p className="text-gray-600">Templates, checklists, and practical tools included</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#a8d5ff] rounded-full flex items-center justify-center mx-auto">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">Inclusive Community</h3>
              <p className="text-gray-600">Collaborative space for sharing ideas and best practices</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;